import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@contexts/auth';
import { BaseService } from '@utility/services/base';
import { APIPath } from '@utility/constants/api';
import {
  type AccountViewResponse,
  type OrdersScopeLevel,
  useOrdersScopeLevel,
} from './scope';

/* EN-4396: whether the signed-in user may see the account level (every
   division's orders), and the level the orders pages should read now. The
   server decides availability (per-account switch + account-wide access);
   a failed or unknown answer means the division level. */
export function useAccountView(): {
  view: AccountViewResponse;
  level: OrdersScopeLevel;
  known: boolean;
} {
  const { currentOrg } = useAuth();
  const stored = useOrdersScopeLevel(currentOrg);
  const query = useQuery<AccountViewResponse>({
    queryKey: ['account-view', currentOrg],
    queryFn: () => BaseService.get(APIPath.ordersAccountView),
    enabled: !!currentOrg,
    retry: false,
    staleTime: 60_000,
  });
  const view: AccountViewResponse = query.data?.available
    ? query.data
    : { available: false };
  return {
    view,
    level: view.available && stored === 'account' ? 'account' : 'division',
    known: query.data !== undefined || query.isError,
  };
}
